import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';

const INPUT_TYPE_ICON = {
  typed: '📝',
  voice: '🎤',
};

// Reusable list of human-context notes attached to a single resource
// (event/media/draft). Spec: docs/specs/FEATURES-ADDITIONS.md §2.10.
//
// Props:
//   resourceType: 'event' | 'media' | 'draft'
//   resourceId:   string (the parent's id)
export function NotesList({ resourceType, resourceId }) {
  const { t, language } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  // ── Load notes whenever the resource changes. ──────────────────────────
  useEffect(() => {
    if (!resourceType || !resourceId) {
      setNotes([]);
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    api.notes
      .list({ resourceType, resourceId })
      .then((data) => {
        if (!cancelled) setNotes(data?.notes ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[notes] load failed:', err.message);
          setLoadError(err);
          setNotes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [resourceType, resourceId]);

  function formatTimestamp(iso) {
    const locale = language === 'pl' ? 'pl-PL' : 'en-GB';
    return new Date(iso).toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  async function handleAdd(content) {
    setPendingId('new');
    try {
      const created = await api.notes.create({
        resourceType,
        resourceId,
        content,
        inputType: 'typed',
      });
      setNotes((prev) => [...prev, created]);
      setAdding(false);
      return null;
    } catch (err) {
      console.error('[notes] create failed:', err);
      return err?.message || t('notes.saveError');
    } finally {
      setPendingId(null);
    }
  }

  async function handleUpdate(id, content) {
    setPendingId(id);
    try {
      const updated = await api.notes.update(id, { content });
      setNotes((prev) => prev.map((n) => (n.id === id ? updated : n)));
      setEditingId(null);
      return null;
    } catch (err) {
      console.error('[notes] update failed:', err);
      return err?.message || t('notes.saveError');
    } finally {
      setPendingId(null);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('notes.confirmDelete'))) return;
    try {
      await api.notes.delete(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      if (editingId === id) setEditingId(null);
    } catch (err) {
      console.error('[notes] delete failed:', err);
    }
  }

  return (
    <section className="notes-list" aria-labelledby="notes-list-heading">
      <header className="notes-list__header">
        <div className="notes-list__heading">
          <h3 id="notes-list-heading">{t('notes.title')}</h3>
          <p>{t('notes.hint')}</p>
        </div>
        {!adding && (
          <button
            type="button"
            className="button button--primary"
            onClick={() => setAdding(true)}
            disabled={isLoading || !resourceId}
          >
            + {t('notes.addButton')}
          </button>
        )}
      </header>

      {loadError && <div className="dev-notice">{t('notes.loadError')}</div>}

      <div className="notes-list__items">
        {isLoading ? (
          <div className="notes-list__empty">{t('notes.loading')}</div>
        ) : notes.length === 0 && !adding ? (
          <div className="notes-list__empty">{t('notes.empty')}</div>
        ) : (
          notes.map((note) =>
            editingId === note.id ? (
              <NoteEditor
                key={note.id}
                initialValue={note.content}
                pending={pendingId === note.id}
                onSave={(content) => handleUpdate(note.id, content)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <NoteRow
                key={note.id}
                note={note}
                timestamp={formatTimestamp(note.updatedAt || note.createdAt)}
                onEdit={() => setEditingId(note.id)}
                onDelete={() => handleDelete(note.id)}
              />
            ),
          )
        )}

        {adding && (
          <NoteEditor
            initialValue=""
            autoFocus
            pending={pendingId === 'new'}
            onSave={handleAdd}
            onCancel={() => setAdding(false)}
          />
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function NoteRow({ note, timestamp, onEdit, onDelete }) {
  const { t } = useTranslation();
  const icon = INPUT_TYPE_ICON[note.inputType] || INPUT_TYPE_ICON.typed;
  return (
    <article className="note-row">
      <div
        className="note-row__icon"
        aria-label={t(`notes.inputType.${note.inputType}`)}
        title={t(`notes.inputType.${note.inputType}`)}
      >
        {icon}
      </div>
      <div className="note-row__body">
        <div className="note-row__content">{note.content}</div>
        <div className="note-row__meta">{timestamp}</div>
      </div>
      <div className="note-row__actions">
        <button
          type="button"
          className="note-row__action"
          onClick={onEdit}
          aria-label={t('notes.edit')}
          title={t('notes.edit')}
        >
          ✏️
        </button>
        <button
          type="button"
          className="note-row__action note-row__action--danger"
          onClick={onDelete}
          aria-label={t('notes.remove')}
          title={t('notes.remove')}
        >
          ✕
        </button>
      </div>
    </article>
  );
}

function NoteEditor({ initialValue, autoFocus, pending, onSave, onCancel }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(null);
  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && !pending;

  async function handleSave() {
    if (!canSave) return;
    const result = await onSave(trimmed);
    if (result) setError(result);
  }

  return (
    <div className="note-editor">
      <textarea
        className="note-editor__textarea"
        value={value}
        placeholder={t('notes.placeholder')}
        onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
        autoFocus={autoFocus}
      />
      {error && <div className="note-editor__error" role="alert">{error}</div>}
      <div className="note-editor__actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={pending}>
          {t('common.cancel')}
        </button>
        <button type="button" className="button button--primary" onClick={handleSave} disabled={!canSave}>
          {pending ? t('notes.saving') : t('notes.save')}
        </button>
      </div>
    </div>
  );
}
